import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Divider,
  Spacer,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Text,
  RadioGroup,
  Stack,
  Radio,
} from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';
import toHex from 'colornames';
import { Button } from '../../';
import PropTypes from 'prop-types';
import {
  Theme,
  updateSettingsForGroup,
  resetDefaultFont,
  resetDefaultColors,
  getSettingsForGroup,
} from '../../../redux/reducers';

const colors = ['black', 'white', 'blue', 'purple', 'green', 'yellow'];
const textColors = colors;
const bgColors = colors;

export const SettingsBody = ({ onClose, onSubmit, mode }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const form = useSelector(getSettingsForGroup(mode), shallowEqual);

  const changeFormValue = (value, field) => {
    dispatch(updateSettingsForGroup(mode, { [field]: value }));
  };

  const resetFormFonts = () => {
    dispatch(resetDefaultFont(mode));
  };
  const resetFormColors = () => {
    dispatch(resetDefaultColors(mode));
  };

  return (
    <Box mx={2}>
      <Box>
        <Trans i18nKey="chat.settings.desc" />
      </Box>
      <Divider my={4} />
      <Flex>
        <FormControl>
          <FormLabel htmlFor="font">{t('chat.settings.form.font')}</FormLabel>

          <Select
            w="48"
            borderRadius={0}
            bg="brand.10"
            name="font"
            onChange={(e) => changeFormValue(e.target.value, 'font')}
            value={form.font}
          >
            <option value="avenir">Avenir</option>
            <option value="Arial, sans-serif">Arial</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel htmlFor="fontsize">{t('chat.settings.form.fontSize')}</FormLabel>

          <Select
            w="48"
            borderRadius={0}
            bg="brand.10"
            name="fontSize"
            onChange={(e) => changeFormValue(e.target.value, 'fontSize')}
            value={form.fontSize}
          >
            <option value="16pt">16 pt</option>
            <option value="24pt">24 pt</option>
            <option value="30pt">30 pt</option>
            {mode === 'interpretation' && (
              <>
                <option value="40pt">40 pt</option>
                <option value="50pt">50 pt</option>
              </>
            )}
          </Select>
        </FormControl>
      </Flex>
      <Text as="button" color="brand.50" my={2} fontWeight="500" onClick={() => resetFormFonts()}>
        <Trans i18nKey="chat.settings.form.reset" />
      </Text>
      <Divider my={4} />
      <Text>
        <Trans i18nKey="chat.settings.colors" />
      </Text>
      <RadioGroup onChange={(val) => changeFormValue(val, 'theme')} value={form.theme}>
        <Stack direction="row">
          <Radio value={Theme.LIGHT}>{t('common.theme.light')}</Radio>
          <Radio value={Theme.DARK}>{t('common.theme.dark')}</Radio>
          <Radio value={Theme.CUSTOM}>{t('common.theme.custom')}</Radio>
        </Stack>
      </RadioGroup>
      <Flex my={4}>
        <FormControl>
          <FormLabel htmlFor="color">{t('chat.settings.form.color')}</FormLabel>
          <Select
            disabled={form.theme !== Theme.CUSTOM}
            w="48"
            borderRadius={0}
            bg="brand.10"
            name="color"
            onChange={(e) => changeFormValue(e.target.value, 'customColor')}
            value={form.customColor}
          >
            <option disabled selected={!form.customColor}>
              {t(`common.color.select`)}
            </option>
            {textColors.map((colorName) => (
              <option key={colorName} value={toHex(colorName)}>
                {t(`common.color.${colorName}`)}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel htmlFor="bgcolor">{t('chat.settings.form.bgColor')}</FormLabel>
          <Select
            disabled={form.theme !== Theme.CUSTOM}
            w="48"
            borderRadius={0}
            bg="brand.10"
            name="bgcolor"
            onChange={(e) => changeFormValue(e.target.value, 'customBgColor')}
            value={form.customBgColor}
          >
            <option disabled selected={!form.customColor}>
              {t(`common.color.select`)}
            </option>
            {bgColors.map((colorName) => (
              <option key={colorName} value={toHex(colorName)}>
                {t(`common.color.${colorName}`)}
              </option>
            ))}
          </Select>
        </FormControl>
      </Flex>

      <Text as="button" color="brand.50" my={2} fontWeight="500" onClick={() => resetFormColors()}>
        <Trans i18nKey="chat.settings.form.reset" />
      </Text>

      <Divider />

      <Flex my={4}>
        <Spacer />
        <Button bg="white" color="brand.50" mx={4} onClick={() => onClose()}>
          <Trans i18nKey="modal.cancel" />
        </Button>
        <Button bg="brand.50" color="white" onClick={() => onSubmit(form)}>
          <Trans i18nKey="modal.save" />
        </Button>
      </Flex>
    </Box>
  );
};

SettingsBody.propTypes = {
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
  mode: PropTypes.string,
};
